import { Vector3 } from 'three';
import type { Runtime } from './runtime';
import type { WorldInit } from './initializeWorld';
import type { SpeechBubble, Townsperson } from '../communication/types';
import { createSpeechLabels } from '../communication/speechLabels';
import { nearbyTownsperson } from '../communication/nearby';
import { townCall, tradeLabel } from '../communication/dialogue';
import { createElement, getUiRoot } from '../ui/dom';
import { currentLanguage, setLabel } from '../ui/i18n';
import { createConversationDialog } from '../ui/conversationDialog';
import { createChatPanel } from '../ui/chatPanel';
import { isChord } from '../controls/keymap';
import { visitorNamePosition } from '../multiplayer/nameLabels';
import { createPlayerInteractions } from './playerInteractions';
import { emoteCommand } from '../multiplayer/emotes';

interface TownSpeech {
  readonly text: string;
  readonly utterance: string;
  readonly until: number;
}

export function createCommunicationRuntime(init: WorldInit, runtime: () => Runtime) {
  const sources = init.world.animated.flatMap((actor) =>
    actor.townspeople ? [actor.townspeople] : []
  );
  const labels = createSpeechLabels(init.pipeline.annotations);
  const speech = new Map<string, TownSpeech>();
  const greeted = new Map<string, number>();
  const direction = new Vector3();
  let nearest: Townsperson | null = null;
  let approached: string | null = null;
  let speechId = 0;
  const position = () => runtime().control.controlState.pose.position;
  const changed = (open: boolean): void => {
    const rt = runtime();
    rt.input?.setEnabled(!open && !rt.menuOpen && !rt.ui.helpOpen);
    if (!open) for (const source of sources) source.converse(null, position());
  };
  const spoken = (person: Townsperson, text: string): void => {
    speech.set(person.id, {
      text,
      utterance: `town-${speechId++}`,
      until: performance.now() + 8000,
    });
    greeted.set(person.id, performance.now());
  };
  const town = createConversationDialog(changed, spoken);
  const chat = createChatPanel(changed, (text, recipient) => {
    const gesture = emoteCommand(text);
    if (gesture) return runtime().emotes.perform(gesture);
    const current = runtime().network;
    return current
      ? current.say(text, recipient)
      : Promise.reject(new Error('The shared city is not connected.'));
  });
  const players = createPlayerInteractions(init, runtime, changed, (peer) => {
    chat.show(peer);
  });
  const talk = createElement('button', 'townspeople-talk', getUiRoot());
  talk.type = 'button';
  talk.hidden = true;
  setLabel(talk, 'title', 'Speak to a townsperson (Shift+Enter)');
  const blocked = (): boolean => {
    const rt = runtime();
    return rt.menuOpen || rt.ui.helpOpen || rt.ui.overlay === 'visible' || rt.emotes.isOpen;
  };
  const speak = (): void => {
    const person = nearest;
    if (blocked() || !person || chat.isOpen || town.isOpen) return;
    if (sources.some((source) => source.converse(person.id, position())))
      town.show(person, runtime().name);
  };
  const openChat = (): void => {
    if (!blocked() && !town.isOpen && !players.isOpen && runtime().network) chat.show();
  };
  const key = (event: KeyboardEvent): void => {
    if (event.code !== 'Enter' || event.repeat || isChord(event)) return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input, textarea, button, select, a, dialog')
    )
      return;
    if (event.shiftKey || runtime().gameMode === 'single') speak();
    else openChat();
    if (chat.isOpen || town.isOpen) event.preventDefault();
  };
  talk.addEventListener('click', speak);
  document.addEventListener('keydown', key, true);
  return {
    openChat,
    get isOpen(): boolean {
      return town.isOpen || chat.isOpen || players.isOpen;
    },
    input: (frame: Parameters<typeof players.input>[0], dt: number) =>
      runtime().emotes.input(players.input(frame, dt)),
    stopFollowing: () => {
      players.stopFollowing();
    },
    diagnostics: () => ({ ...players.diagnostics(), bubbles: labels.diagnostics() }),
    update(): void {
      const rt = runtime();
      const people = sources.flatMap((source) => source.list());
      const now = performance.now();
      const active = !blocked();
      if (!active) {
        town.hide();
        chat.hide();
      }
      const speaking = town.person;
      if (speaking && !people.some((person) => person.id === speaking.id)) town.hide();
      init.camera.getWorldDirection(direction);
      nearest =
        active && rt.control.controlState.mode === 'walk'
          ? nearbyTownsperson(people, position(), direction.toArray(), rt.env.env.collision)
          : null;
      talk.hidden = !nearest || town.isOpen || chat.isOpen;
      if (nearest)
        talk.textContent = `${tradeLabel(nearest, currentLanguage())} · ${nearest.name} (⇧ Enter)`;
      if (
        nearest &&
        nearest.id !== approached &&
        !town.isOpen &&
        !chat.isOpen &&
        now - (greeted.get(nearest.id) ?? -Infinity) > 30000
      )
        spoken(nearest, townCall(nearest, currentLanguage()));
      approached = nearest?.id ?? null;
      const bubbles: SpeechBubble[] = [];
      for (const person of people) {
        const line = speech.get(person.id);
        if (line && line.until > now)
          bubbles.push({
            id: person.id,
            ...line,
            position: [
              person.position[0],
              person.position[1] + person.height + 0.15,
              person.position[2],
            ],
          });
        else if (line) speech.delete(person.id);
      }
      const network = rt.network;
      if (network) {
        const remote = new Map(network.speakers().map((peer) => [peer.id, peer]));
        const own = network.diagnostics().id;
        for (const line of [...network.speech.active(network.serverEpoch())].reverse()) {
          const peer = remote.get(line.speaker);
          if (!peer && line.speaker !== own) continue;
          const current = peer ?? {
            x: position()[0],
            y: position()[1],
            z: position()[2],
            mode: rt.control.controlState.mode === 'boat' ? ('boat' as const) : ('walk' as const),
          };
          const at = visitorNamePosition(current);
          at[1] += 0.3;
          bubbles.push({
            id: line.id,
            utterance: line.id,
            text: line.text,
            position: at,
            kind: line.kind,
            stack: line.speaker,
          });
        }
      }
      bubbles.sort(
        (a, b) =>
          Math.hypot(a.position[0] - position()[0], a.position[2] - position()[2]) -
          Math.hypot(b.position[0] - position()[0], b.position[2] - position()[2])
      );
      labels.update(active ? bubbles : []);
      players.update();
      rt.emotes.update();
    },
    dispose(): void {
      document.removeEventListener('keydown', key, true);
      town.dispose();
      chat.dispose();
      labels.dispose();
      players.dispose();
      talk.remove();
    },
  };
}
